import { getDefinition, type DefLookup } from "./catalog";
import { dcChargingPortGroups, isDirectDcSource } from "./component-ports";
import { buildDcCircuitTopology } from "./dc-circuit-topology";
import { effectiveTerminals } from "./effective";
import { v } from "./diagnostics/graph";
import type { LoadCircuit } from "./load-circuits";
import type { ComponentDefinition, System } from "./types";

export type ChargerOutputCircuit = LoadCircuit & {
  chargerId: string;
  portId: string;
  nominalV: number;
  manufacturerAllowancePct?: number;
};

// Exact models covered by SmartSolar 75/10–100/20 manual §8.6.3: compare
// charger battery terminals with battery terminals at full charge current;
// more than 2.5% battery-cable loss is unacceptable. This is not a universal
// charger/ABYC allowance. No persisted metadata or name-based inference.
// https://www.victronenergy.com/media/pg/Manual_SmartSolar_MPPT_75-10_up_to_100-20/en/troubleshooting.html
const SMARTSOLAR: Record<string, { model: string; amps: number; voc: number }> = {
  "mppt-victron-7510": { model: "SmartSolar 75/10", amps: 10, voc: 75 },
  "mppt-victron-7515": { model: "SmartSolar 75/15", amps: 15, voc: 75 },
  "mppt-victron-10015": { model: "SmartSolar 100/15", amps: 15, voc: 100 },
  "mppt-victron-10020": { model: "SmartSolar 100/20", amps: 20, voc: 100 },
};
function manufacturerAllowance(def: ComponentDefinition, positive: string[], negative: string[]): number | undefined {
  const profile = def.category === "charger" && Object.hasOwn(SMARTSOLAR, def.id) ? SMARTSOLAR[def.id] : undefined;
  // A customised catalog id can outlive changes to the actual part. Retained
  // identity and relevant electrical declarations must still agree. Terminal
  // layout edits are harmless; a changed output role/shape is not the model.
  if (!profile || def.manufacturer !== "Victron" || def.model !== profile.model ||
    def.ampOutput !== profile.amps || def.pvMaxVoc !== profile.voc ||
    positive.length !== 1 || positive[0] !== "batt-pos" ||
    negative.length !== 1 || negative[0] !== "batt-neg") return undefined;
  const terms = ["batt-pos", "batt-neg"].map(id => def.terminals.find(t => t.id === id));
  return terms.every(t => t?.direction === "output" && t.maxAmp === profile.amps) ? 2.5 : undefined;
}

export function chargerCircuitAllowancePct(circuit: ChargerOutputCircuit, targetPct: number): number {
  return Math.min(targetPct, circuit.manufacturerAllowancePct ?? targetPct);
}
const cache = new WeakMap<System, WeakMap<DefLookup, ChargerOutputCircuit[]>>();

/** Complete external charging-output-to-battery connections. The shared physical
 * topology pairs both legs with the SAME battery source and never crosses a
 * converter/isolator as copper. Series bank interconnects are included conservatively in the complete loop,
 * beyond the external-terminal measurement described in the manual.
 * Direct DC sources and engine alternator outputs share the configured planning
 * target; their rated envelope is not measured charging current or voltage loss.
 * Source alternatives are design bounds, not predicted current sharing. */
export function collectChargerOutputCircuits(system: System, lookup: DefLookup = getDefinition): ChargerOutputCircuit[] {
  let byLookup = cache.get(system);
  if (!byLookup) { byLookup = new WeakMap(); cache.set(system, byLookup); }
  const known = byLookup.get(lookup);
  if (known) return known;
  const topology = buildDcCircuitTopology(system, lookup);
  const definitions = new Map(system.nodes.flatMap(node => node.kind === "component"
    ? [[node.id, lookup(node.definitionId, node.customDefinition)] as const] : []));
  const result: ChargerOutputCircuit[] = [];
  for (const node of system.nodes) {
    if (node.kind !== "component") continue;
    const declared = definitions.get(node.id);
    if (!declared || (declared.category !== "charger" && !isDirectDcSource(declared)) || !(declared.ampOutput != null && Number.isFinite(declared.ampOutput) && declared.ampOutput > 0)) continue;
    const def = { ...declared, terminals: effectiveTerminals(declared, node) };
    for (const [index, terms] of dcChargingPortGroups(def).entries()) {
      const positiveIds = terms.filter(t => t.polarity === "positive").map(t => t.id);
      const negativeIds = terms.filter(t => t.polarity === "negative" || t.polarity === "ground").map(t => t.id);
      if (!positiveIds.length || !negativeIds.length) continue;
      const portId = `${node.id}:output:${index}`;
      const resolution = topology.resolvePort({ id: portId, nodeId: node.id,
        positive: positiveIds.map(id => v(node.id, id)), negative: negativeIds.map(id => v(node.id, id)) });
      if (resolution.unsupported.length) continue;
      for (const route of resolution.routes) {
        if (route.source.kind !== "battery") continue;
        // The topology's legacy nominal fallback is useful elsewhere but is
        // not evidence for a verified charger percentage on unknown banks.
        if (!route.source.batteryNodeIds.every(id => {
          const voltage = definitions.get(id)?.voltage;
          return voltage != null && Number.isFinite(voltage) && voltage > 0;
        })) continue;
        const nominalV = route.source.nominalV;
        if (nominalV == null || !Number.isFinite(nominalV) || nominalV <= 0) continue;
        const pos = [...route.positive].reverse().map(step => step.cableId);
        pos.push(...route.series.map(step => step.cableId));
        const neg = route.negative.map(step => step.cableId);
        if (!pos.length || !neg.length) continue;
        result.push({ chargerId: node.id, portId, loadVertex: route.positiveTerminal, pos, neg,
          complete: true, sourceId: route.sourceId, nominalV,
          manufacturerAllowancePct: manufacturerAllowance(def, positiveIds, negativeIds) });
      }
    }
  }
  byLookup.set(lookup, result);
  return result;
}
